import type {
	DoRawQueryResult,
	DoSqlWithParams,
} from "../../workers/local-explorer/generated";

export const CORE_PLUGIN_NAME = "core";

// Service for HTTP socket entrypoint (for checking runtime ready, routing, etc)
export const SERVICE_ENTRY = `${CORE_PLUGIN_NAME}:entry`;
// Service for local explorer (API + UI)
export const SERVICE_LOCAL_EXPLORER = `${CORE_PLUGIN_NAME}:local-explorer`;
// Disk service for local explorer UI assets
export const LOCAL_EXPLORER_DISK = `${CORE_PLUGIN_NAME}:local-explorer-disk`;
// Local observability collector; user workers' `streamingTails` point here.
// Prefixed via getUserServiceName(), so the name itself must not contain a
// colon (it collides with the `core:user:` service namespacing).
export const OBSERVABILITY_COLLECTOR_SERVICE_NAME =
	"miniflare-observability-collector";
// Flags that make a user worker stream its tail (incl. user spans) to the
// collector; applied to each user worker when observability is enabled
export const OBSERVABILITY_COMPAT_FLAGS = [
	"streaming_tail_worker",
	"tail_worker_user_spans",
] as const;
// Service prefix for all regular user workers
const SERVICE_USER_PREFIX = `${CORE_PLUGIN_NAME}:user`;
// Service prefix for `workerd`'s builtin services (network, external, disk)
const SERVICE_BUILTIN_PREFIX = `${CORE_PLUGIN_NAME}:builtin`;
// Service prefix for custom fetch functions defined in `serviceBindings` option
const SERVICE_CUSTOM_FETCH_PREFIX = `${CORE_PLUGIN_NAME}:custom-fetch`;
// Service prefix for custom Node functions defined in `serviceBindings` option
const SERVICE_CUSTOM_NODE_PREFIX = `${CORE_PLUGIN_NAME}:custom-node`;

export function getUserServiceName(workerName = "") {
	return `${SERVICE_USER_PREFIX}:${workerName}`;
}

// Namespace custom services to avoid conflicts between user-specified names
// and hardcoded Miniflare names
export enum CustomServiceKind {
	UNKNOWN = "#", // User specified name (i.e. `serviceBindings`)
	KNOWN = "$", // Miniflare specified name (i.e. `outboundService`)
}

export const CUSTOM_SERVICE_KNOWN_OUTBOUND = "outbound";

export function getBuiltinServiceName(
	workerIndex: number,
	kind: CustomServiceKind,
	bindingName: string
) {
	return `${SERVICE_BUILTIN_PREFIX}:${workerIndex}:${kind}${bindingName}`;
}

export function getCustomFetchServiceName(
	workerIndex: number,
	kind: CustomServiceKind,
	bindingName: string
) {
	return `${SERVICE_CUSTOM_FETCH_PREFIX}:${workerIndex}:${kind}${bindingName}`;
}

export function getCustomNodeServiceName(
	workerIndex: number,
	kind: CustomServiceKind,
	bindingName: string
) {
	return `${SERVICE_CUSTOM_NODE_PREFIX}:${workerIndex}:${kind}${bindingName}`;
}

/**
 * Used by the local explorer worker.
 * The method name injected into wrapped Durable Objects for SQLite introspection.
 */
export const INTROSPECT_SQLITE_METHOD = "__miniflare_introspectSqlite";

export type IntrospectSqliteMethod = (
	queries: DoSqlWithParams[]
) => Promise<DoRawQueryResult[]>;

/**
 * Used by the local explorer worker.
 * This method is injected into wrapped Durable Objects to allow getting the DO instance name.
 */
export const GET_DO_NAME_METHOD = "__miniflare_getDOName";

export type GetDONameMethod = () => string | undefined;
